import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Context/AuthContext';
import Calendar from '../Calendar/Calendar';
import WeekendDuty from '../WeekendDuty/WeekendDuty';
import TaskManager from '../Tasks/TaskManager';
import AppointmentScheduler from '../Appointments/AppointmentScheduler';
import DatabaseTest from '../DatabaseTest/DatabaseTest';
import UserManagement from '../UserManagement/UserManagement';
import './Dashboard.css';

const VALID_TABS = ['calendar', 'weekend-duty', 'tasks', 'appointments', 'database-test', 'user-management'];

const getTabFromHash = () => {
    const hash = window.location.hash.replace(/^#\/?/, '');
    return VALID_TABS.includes(hash) ? hash : 'calendar';
};

const Dashboard = () => {
    const { logout, currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState(getTabFromHash);

    // PWA Install state
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showInstallBtn, setShowInstallBtn] = useState(false);
    const [showIosGuide, setShowIosGuide] = useState(false);

    useEffect(() => {
        // Ensure URL hash is set on mount
        const initialTab = getTabFromHash();
        if (!window.location.hash || window.location.hash === '#/') {
            window.location.hash = `#/${initialTab}`;
        }

        const handleHashChange = () => {
            const newTab = getTabFromHash();
            setActiveTab(newTab);
        };

        window.addEventListener('hashchange', handleHashChange);

        // Check if app is already running in standalone mode (installed)
        const checkStandalone = () => {
            const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
                window.navigator.standalone === true ||
                document.referrer.includes('android-app://');
            return isStandaloneMode;
        };

        const isAlreadyInstalled = checkStandalone();
        if (!isAlreadyInstalled) {
            setShowInstallBtn(true);
        }

        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            if (!isAlreadyInstalled) {
                setShowInstallBtn(true);
            }
        };

        const handleAppInstalled = () => {
            setShowInstallBtn(false);
            setDeferredPrompt(null);
            setShowIosGuide(false);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            deferredPrompt.userChoice.then((choiceResult) => {
                if (choiceResult.outcome === 'accepted') {
                    setShowInstallBtn(false);
                }
                setDeferredPrompt(null);
            });
        } else {
            setShowIosGuide(true);
        }
    };

    const handleTabChange = (tab) => {
        window.location.hash = `#/${tab}`;
        setActiveTab(tab);
    };

    const handleLogout = () => {
        logout();
    };

    // Check if current user is Rich (admin)
    const isAdmin = currentUser && currentUser.username === 'rich_w';

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'calendar':
                return <Calendar />;
            case 'weekend-duty':
                return <WeekendDuty />;
            case 'tasks':
                return <TaskManager />;
            case 'appointments':
                return <AppointmentScheduler />;
            case 'database-test':
                return <DatabaseTest />;
            case 'user-management':
                return <UserManagement />;
            default:
                return <Calendar />;
        }
    };

    return (
        <div className="dashboard">
            <header className="dashboard-header">
                <div className="header-content">
                    <h1>Family Calendar & Task Manager</h1>
                    <div className="header-actions">
                        {showInstallBtn && (
                            <button onClick={handleInstallClick} className="install-pwa-btn" title="Install App to Home Screen">
                                📱 Install App
                            </button>
                        )}
                        {currentUser && (
                            <div className="user-info">
                                <span className="user-name">Welcome, {currentUser.name}</span>
                                {isAdmin && <span className="admin-badge">Admin</span>}
                            </div>
                        )}
                        <button onClick={handleLogout} className="logout-btn">
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            {/* PWA Add to Home Screen Instructions Modal */}
            {showIosGuide && (
                <div className="pwa-modal-overlay" onClick={() => setShowIosGuide(false)}>
                    <div className="pwa-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="pwa-modal-header">
                            <img src="/app-icon.png" alt="App Icon" className="pwa-modal-icon" />
                            <h3>Install Family Calendar</h3>
                            <button className="pwa-close-btn" onClick={() => setShowIosGuide(false)}>×</button>
                        </div>
                        <div className="pwa-modal-body">
                            <p><strong>iPhone / iPad (Safari):</strong></p>
                            <ol>
                                <li>Tap the <strong>Share</strong> button 📤 at the bottom of Safari.</li>
                                <li>Scroll down and select <strong>Add to Home Screen</strong> ➕.</li>
                            </ol>
                            <p><strong>Android (Chrome / Edge):</strong></p>
                            <ol>
                                <li>Tap the menu <strong>⋮</strong> in the top right corner.</li>
                                <li>Select <strong>Add to Home screen</strong> or <strong>Install app</strong>.</li>
                            </ol>
                            <p><strong>Desktop (Chrome / Edge):</strong></p>
                            <ol>
                                <li>Click the <strong>Install</strong> icon ↗️ in the right side of the URL address bar.</li>
                            </ol>
                        </div>
                        <button className="pwa-gotit-btn" onClick={() => setShowIosGuide(false)}>Got It</button>
                    </div>
                </div>
            )}

            <nav className="dashboard-nav">
                <div className="nav-tabs-container">
                    <button
                        className={`nav-tab ${activeTab === 'calendar' ? 'active' : ''}`}
                        onClick={() => handleTabChange('calendar')}
                    >
                        <span className="tab-icon">📅</span>
                        <span className="tab-label">Calendar</span>
                    </button>
                    <button
                        className={`nav-tab ${activeTab === 'weekend-duty' ? 'active' : ''}`}
                        onClick={() => handleTabChange('weekend-duty')}
                    >
                        <span className="tab-icon">🏠</span>
                        <span className="tab-label">Weekend Duty</span>
                    </button>
                    <button
                        className={`nav-tab ${activeTab === 'tasks' ? 'active' : ''}`}
                        onClick={() => handleTabChange('tasks')}
                    >
                        <span className="tab-icon">✅</span>
                        <span className="tab-label">Tasks</span>
                    </button>
                    <button
                        className={`nav-tab ${activeTab === 'appointments' ? 'active' : ''}`}
                        onClick={() => handleTabChange('appointments')}
                    >
                        <span className="tab-icon">🏥</span>
                        <span className="tab-label">Appointments</span>
                    </button>

                    {/* Admin-only tabs */}
                    {isAdmin && (
                        <>
                            <button
                                className={`nav-tab ${activeTab === 'database-test' ? 'active' : ''}`}
                                onClick={() => handleTabChange('database-test')}
                            >
                                <span className="tab-icon">🗄️</span>
                                <span className="tab-label">Database</span>
                            </button>
                            <button
                                className={`nav-tab ${activeTab === 'user-management' ? 'active' : ''}`}
                                onClick={() => handleTabChange('user-management')}
                            >
                                <span className="tab-icon">👥</span>
                                <span className="tab-label">Users</span>
                            </button>
                        </>
                    )}
                </div>
            </nav>

            <main className="dashboard-content">
                {renderActiveTab()}
            </main>
        </div>
    );
};

export default Dashboard;
